import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Button } from '~/shadcn/components/ui/Button';
import { Input } from '~/shadcn/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/shadcn/components/ui/Dialog';
import { UserContext } from '~/contexts/UserContext';
import { CycleCountApi } from '~/api/cycleCount';
import { fbmApi } from '~/api/fbm';
import { PartnerCycleCountForm } from './partnerCycleCountForm';
import { PartnerCycleCountDetailsModal } from './partnerCycleCountDetailsModal';
import { toast } from 'sonner';
import { downloadCSV } from '~/shared/components/csvDownload';
import dayjs from '~/utils/dayjsConfig';

const cycleCountApi = new CycleCountApi();

const DEFAULT_EXCLUDED_NAMES = ['gopuff', 'doordash', 'imperfect', 'metabolic'];

const PartnerCycleCountTab = () => {
    const { user } = useContext(UserContext);
    const [rows, setRows] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedRequestDetails, setSelectedRequestDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [requestIdToCancel, setRequestIdToCancel] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const getSourceOptions = async () => {
        if (!user?.facilityID) {
            return;
        }

        const res = await fbmApi.getFBMAccounts('', false, false, true, user.facilityID);
        if (!res.status) {
            toast.error('Failed to get company sources');
            return;
        }

        const filteredAccounts = res.data.accounts.filter(
            (account) => !DEFAULT_EXCLUDED_NAMES.includes(account.fbmAccountName),
        );

        const options = [
            {
                value: 'MISFITS_MARKET',
                label: 'Misfits Market',
            },
            ...filteredAccounts.map((account) => ({
                value: account.fbmAccountName,
                label: account.companyName,
            })),
        ];

        setSourceOptions(options);
    };

    const getPartnerCycleCountRows = async () => {
        const res = await cycleCountApi.getPartnerCycleCountRequests(user.facilityID);
        if (!res.status) {
            toast.error(res.msg || 'Failed to load partner cycle counts');
            return;
        }

        setRows(res.data.requests);
    };

    useEffect(() => {
        if (user?.facilityID) {
            getSourceOptions();
            getPartnerCycleCountRows();
        }
    }, [user?.facilityID]);

    const handleCreateSuccess = () => {
        toast.success('Cycle count request created');
        setModalOpen(false);
        getPartnerCycleCountRows();
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleRowClick = async (requestId) => {
        setLoadingDetails(true);
        setDetailsModalOpen(true);

        const res = await cycleCountApi.getPartnerCycleCountDetails(requestId);
        setLoadingDetails(false);
        if (!res.status || !res.data?.details) {
            toast.error(res.msg || 'Failed to load cycle count details');
            return;
        }

        setSelectedRequestDetails(res.data.details);
    };

    const handleCloseDetailsModal = () => {
        setDetailsModalOpen(false);
        setSelectedRequestDetails(null);
    };

    const handleOpenCancelModal = (e, requestId) => {
        e.stopPropagation();
        setRequestIdToCancel(requestId);
        setCancelModalOpen(true);
    };

    const handleCloseCancelModal = () => {
        setCancelModalOpen(false);
        setRequestIdToCancel(null);
    };

    const handleCancel = async () => {
        const res = await cycleCountApi.cancelCycleCountRequest(requestIdToCancel);
        if (!res.status) {
            toast.error(res.msg || 'Failed to cancel cycle count request');
            setCancelModalOpen(false);
            setRequestIdToCancel(null);
            return;
        }
        toast.success('Cycle count request cancelled');
        setCancelModalOpen(false);
        setRequestIdToCancel(null);
        getPartnerCycleCountRows();
    };

    const getSourceLabel = (value) => {
        return sourceOptions.find((option) => option.value === value)?.label || value;
    };

    const formatCompanySources = (companySources) => {
        return companySources.map((source) => getSourceLabel(source)).join(', ');
    };

    const calculatePercentComplete = (numActualResults, numExpectedResults) => {
        if (!numExpectedResults || numExpectedResults === 0) {
            return 0;
        }
        return Math.round((numActualResults / numExpectedResults) * 100);
    };

    // Fuzzy search filter function
    const filterRows = (rowsToFilter, query) => {
        if (!query || query.trim() === '') {
            return rowsToFilter;
        }

        const searchTerm = query.toLowerCase().trim();

        return rowsToFilter.filter((row) => {
            // Search across all relevant fields
            const searchableFields = [
                row.requestId?.toString() || '',
                row.createdBy || '',
                row.status || '',
                formatCompanySources(row.companySources),
                row.skusExpected?.toString() || '',
                row.skusCounted?.toString() || '',
                row.unitsExpected?.toString() || '',
                row.unitsCounted?.toString() || '',
                row.lotsExpected?.toString() || '',
                row.lotsCounted?.toString() || '',
                row.skuAccuracy?.toString() || '',
                row.unitAccuracy?.toString() || '',
                row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '',
                row.completedAt ? new Date(row.completedAt).toLocaleDateString() : '',
            ];

            // Check if search term appears in any field
            return searchableFields.some((field) => field.toLowerCase().includes(searchTerm));
        });
    };

    // Filtered rows based on search query
    const filteredRows = useMemo(() => {
        return filterRows(rows, searchQuery);
    }, [rows, searchQuery]);

    // Reset to first page when search changes
    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const handleExportParentTable = () => {
        const rowsToExport = filteredRows.length > 0 ? filteredRows : rows;

        if (!rowsToExport || rowsToExport.length === 0) {
            toast.error('No data to export');
            return;
        }

        const headers = [
            'ID',
            'Date Requested',
            'Date Completed',
            'Manager',
            'Status',
            'Partners',
            'Percent Complete',
            'Skus Expected',
            'Skus Accurate',
            'Units Expected',
            'Units Counted',
            'Lots Expected',
            'Lots Accurate',
            'Sku Accuracy',
            'Unit Accuracy',
        ];

        let csvContent = headers.join(',') + '\r\n';

        rowsToExport.forEach((row) => {
            const dateRequested = row.createdAt
                ? `${new Date(row.createdAt).toLocaleDateString()} ${new Date(row.createdAt).toLocaleTimeString()}`
                : '';
            const dateCompleted = row.completedAt
                ? `${new Date(row.completedAt).toLocaleDateString()} ${new Date(row.completedAt).toLocaleTimeString()}`
                : '';
            const partners = formatCompanySources(row.companySources);
            const percentComplete = calculatePercentComplete(row.numActualResults, row.numExpectedResults);

            const rowData = [
                row.requestId || '',
                `"${dateRequested}"`,
                `"${dateCompleted}"`,
                `"${row.createdBy || ''}"`,
                `"${row.status || ''}"`,
                `"${partners}"`,
                percentComplete + '%',
                row.skusExpected || 0,
                row.skusCounted || 0,
                row.unitsExpected || 0,
                row.unitsCounted || 0,
                row.lotsExpected || 0,
                row.lotsCounted || 0,
                row.skuAccuracy || '',
                row.unitAccuracy || '',
            ];

            csvContent += rowData.join(',') + '\r\n';
        });

        const timestamp = dayjs().format('YYYY_MM_DD_HH_mm');
        const searchSuffix = searchQuery ? `_filtered_${searchQuery.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
        const filename = `partner_cycle_count_requests${searchSuffix}_${timestamp}.csv`;
        downloadCSV(csvContent, filename);
        toast.success(`Export completed (${rowsToExport.length} row${rowsToExport.length !== 1 ? 's' : ''})`);
    };

    const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <div>
            <div className="flex justify-between mb-4 gap-4">
                <div className="flex-1 max-w-md">
                    <Input
                        type="text"
                        placeholder="Search across all columns..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                    />
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportParentTable} disabled={!rows || rows.length === 0}>
                        Export Table{searchQuery ? ` (${filteredRows.length})` : ''}
                    </Button>
                    <Button onClick={() => setModalOpen(true)}>Create New</Button>
                </div>
            </div>
            <PartnerCycleCountForm
                open={modalOpen}
                onSuccess={handleCreateSuccess}
                closeModal={handleCloseModal}
                sourceOptions={sourceOptions}
            />
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>Date Requested</TableHead>
                            <TableHead>Date Completed</TableHead>
                            <TableHead>Manager</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Partners</TableHead>
                            <TableHead>Percent Complete</TableHead>
                            <TableHead>Skus Expected</TableHead>
                            <TableHead>Skus Accurate</TableHead>
                            <TableHead>Units Expected</TableHead>
                            <TableHead>Units Counted</TableHead>
                            <TableHead>Lots Expected</TableHead>
                            <TableHead>Lots Accurate</TableHead>
                            <TableHead>Sku Accuracy</TableHead>
                            <TableHead>Unit Accuracy</TableHead>
                            <TableHead>Cancel</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedRows.length > 0 ? (
                            paginatedRows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => handleRowClick(row.requestId)}
                                >
                                    <TableCell className="text-left">{row.requestId}</TableCell>
                                    <TableCell className="text-left">
                                        {row.createdAt ? (
                                            <div className="flex flex-col">
                                                <span>{new Date(row.createdAt).toLocaleDateString()}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {new Date(row.createdAt).toLocaleTimeString()}
                                                </span>
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                    </TableCell>
                                    <TableCell className="text-left">
                                        {row.completedAt ? (
                                            <div className="flex flex-col">
                                                <span>{new Date(row.completedAt).toLocaleDateString()}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {new Date(row.completedAt).toLocaleTimeString()}
                                                </span>
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                    </TableCell>
                                    <TableCell className="text-left">{row.createdBy}</TableCell>
                                    <TableCell className="text-left">{row.status}</TableCell>
                                    <TableCell className="text-left">
                                        {formatCompanySources(row.companySources)}
                                    </TableCell>
                                    <TableCell className="text-left">
                                        {calculatePercentComplete(row.numActualResults, row.numExpectedResults)}%
                                    </TableCell>
                                    <TableCell className="text-left">{row.skusExpected}</TableCell>
                                    <TableCell className="text-left">{row.skusCounted}</TableCell>
                                    <TableCell className="text-left">{row.unitsExpected}</TableCell>
                                    <TableCell className="text-left">{row.unitsCounted}</TableCell>
                                    <TableCell className="text-left">{row.lotsExpected}</TableCell>
                                    <TableCell className="text-left">{row.lotsCounted}</TableCell>
                                    <TableCell className="text-left">{row.skuAccuracy}</TableCell>
                                    <TableCell className="text-left">{row.unitAccuracy}</TableCell>
                                    <TableCell className="text-left">
                                        {['NEW', 'IN PROGRESS'].includes(row.status) && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => handleOpenCancelModal(e, row.requestId)}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Cycle Count Request</DialogTitle>
                        <DialogDescription>Are you sure you want to cancel this cycle count request?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={handleCloseCancelModal}>
                            No, Keep It
                        </Button>
                        <Button onClick={handleCancel}>Yes, Cancel Request</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {(rows.length > 0 || filteredRows.length > 0) && (
                <div className="flex items-center justify-between py-4">
                    <div className="text-sm text-muted-foreground">
                        Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredRows.length)} of{' '}
                        {filteredRows.length} result{filteredRows.length !== 1 ? 's' : ''}
                        {searchQuery && filteredRows.length !== rows.length && (
                            <span> (filtered from {rows.length} total)</span>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleChangePage(page - 1)}
                            disabled={page === 0}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleChangePage(page + 1)}
                            disabled={page >= Math.ceil(filteredRows.length / rowsPerPage) - 1}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}

            <PartnerCycleCountDetailsModal
                open={detailsModalOpen}
                onClose={handleCloseDetailsModal}
                loading={loadingDetails}
                details={selectedRequestDetails}
            />
        </div>
    );
};

export default PartnerCycleCountTab;
